<script lang="ts">
    import type {FormControlOptions} from '../../forms/types';
    import type {ValueEditorsEventMap} from '../../../events';
    import type {ObjectValueEditorField} from './types';
    import type {ValueEditorBindings, ValueEditorValidations} from '../../../kp-value-editor/types';
    import type {ObjectValueEditorOptions} from './types';
    import type {FormGroup} from '../../forms/form-group';
    import type {ValueEditorSize} from '../../../types';
    import {uuid as generateUuid} from '../../../../utils/custom-utils';
    import {onMount, setContext} from 'svelte';
    import {FormGroupBuilder} from '../../forms/form-group';
    import {validatorFactory} from '../../../kp-value-editor/utils';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {createEventDispatcher} from 'svelte';
    import ObjectValueEditorFieldComponent from './ObjectValueEditorField.svelte';
    import {FORM_STRUCTURE_CHANGE} from '../../../events';
    import {FORM_BUILDER} from '../../../context-keys';
    import {cloneDeep} from 'lodash-es';

    type MODEL_VALUE = $$Generic<Record<string, unknown>>;

    export let model: MODEL_VALUE;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;

    export let placeholder: string;
    export let isDisabled: boolean;
    export let isVisible: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: ObjectValueEditorOptions<MODEL_VALUE>;
    export let validations: ValueEditorValidations;

    ignoreUnusedProperties(placeholder);

    const dispatch = createEventDispatcher<ValueEditorsEventMap<MODEL_VALUE>>();
    const labelContext = getValueEditorLabelContext();
    const formGroupBuilder = new FormGroupBuilder<typeof model>();
    formGroupBuilder.addValidators(validatorFactory(validations));
    setContext(FORM_BUILDER, formGroupBuilder);
    let formGroup: FormGroup<typeof model>;

    model = normalizeModelIfNeeded(model);

    onMount(() => {
        const map = new Map<string, keyof typeof model>();
        fields.forEach((field) => map.set(field.editor.editorName, field.fieldName));
        formGroup = formGroupBuilder.build(editorId, editorName, map);
    });

    const focusedFields = options?.fields?.filter((field) => field.editor.isFocused) ?? [];
    const shouldFocusSpecificEditor = focusedFields.length > 0;
    if (focusedFields.length > 1) {
        focusedFields.slice(1).forEach((field) => field.editor.isFocused = false); // only 1 field can be focused, unset focus from others
    }
    let fields: ObjectValueEditorField<MODEL_VALUE>[];
    $: fields = options.fields.map((field, index) => modifyFieldEditor(field, isDisabled, isVisible, index));

    $: model = normalizeModelIfNeeded(model);

    function normalizeModelIfNeeded(modelValue: typeof model): typeof model {
        if (!modelValue) {
            return {} as MODEL_VALUE; // if model is undefined/null initialize it as empty object (to prevent null reference errors)
        }
        return modelValue;
    }

    function modifyFieldEditor(field: ObjectValueEditorField<MODEL_VALUE>, disabled: boolean, visible: boolean, index: number): ObjectValueEditorField<MODEL_VALUE> {
        if (typeof options.attributesTransformation === 'function') {
            field.editor = options.attributesTransformation(field.editor);
        }

        field.editor.editorId ??= generateUuid(); // generate missing editor id
        field.editor.editorName ??= (field.fieldName as any).toString(); // use field name as editor name if it is missing
        field.editor.size ??= size;
        field.editor.isDisabled = disabled || (field.editor.isDisabled ?? false);
        field.editor.isVisible = visible && (field.editor.isVisible ?? true);
        field.editor.isFocused = resolveIsFocused(field.editor, index);

        return field;
    }

    function resolveIsFocused(fieldEditor: ValueEditorBindings, index: number): boolean {
        if (!shouldFocusSpecificEditor && isFocused && index === 0) { // focus first editor
            return true;
        }

        return shouldFocusSpecificEditor && fieldEditor.isFocused; // focus editor with predefined focus
    }

    function updateForm() {
        formGroup.recomposeController();
        dispatch(FORM_STRUCTURE_CHANGE);
    }

    export function getFormController(): FormGroup<MODEL_VALUE> {
        return formGroup;
    }
</script>

<fieldset class="form-horizontal" name={editorName} id={editorId} use:ariaLabelledby={labelContext}>
    {#each fields as field}
        <ObjectValueEditorFieldComponent
            bind:model={model[field.fieldName]}

            {field}
            labelsWidth={options.labelsWidth}
            formControlOptions={cloneDeep(formControlOptions)}
            {forceShowErrors}

            on:form-submit-request
            on:form-structure-change={updateForm}/>
    {/each}
</fieldset>